import type { OrderStage, OrderPaymentStatus } from "./statuses.lib";
import { isOneOf } from "@shared/validation/is-one-of";

export const PIPELINE_STATUS = {
  DRAFT: "draft",
  AWAITING_PAYMENT: "awaiting_payment",
  AWAITING_APPROVAL: "awaiting_approval",
  PAYMENT_FAILED: "payment_failed",
  PENDING_CONFIRMATION: "pending_confirmation",
  CONFIRMED: "confirmed",
  SCHEDULED: "scheduled",
  SHIPPED: "shipped",
  PARTIALLY_DELIVERED: "partially_delivered",
  DELIVERED: "delivered",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

export type PipelineStatus =
  (typeof PIPELINE_STATUS)[keyof typeof PIPELINE_STATUS];

export const PIPELINE_STATUSES: readonly PipelineStatus[] = [
  PIPELINE_STATUS.DRAFT,
  PIPELINE_STATUS.AWAITING_PAYMENT,
  PIPELINE_STATUS.AWAITING_APPROVAL,
  PIPELINE_STATUS.PAYMENT_FAILED,
  PIPELINE_STATUS.PENDING_CONFIRMATION,
  PIPELINE_STATUS.CONFIRMED,
  PIPELINE_STATUS.SCHEDULED,
  PIPELINE_STATUS.SHIPPED,
  PIPELINE_STATUS.PARTIALLY_DELIVERED,
  PIPELINE_STATUS.DELIVERED,
  PIPELINE_STATUS.COMPLETED,
  PIPELINE_STATUS.CANCELLED,
] as const;

export const ORDER_FLAG = {
  OVERDUE: "overdue",
  DISPUTED: "disputed",
  PARTIAL_REFUND: "partial_refund",
  PAYMENT_FAILED: "payment_failed",
  ACCOUNTING_DECLINED: "accounting_declined",
} as const;

export type OrderFlag = (typeof ORDER_FLAG)[keyof typeof ORDER_FLAG];

export const PIPELINE_PHASE = {
  DRAFT: "draft",
  PAYMENT: "payment",
  CONFIRMATION: "confirmation",
  FULFILLMENT: "fulfillment",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

export type PipelinePhase =
  (typeof PIPELINE_PHASE)[keyof typeof PIPELINE_PHASE];

export const PIPELINE_STATUS_TO_PHASE: Record<PipelineStatus, PipelinePhase> = {
  draft: PIPELINE_PHASE.DRAFT,
  awaiting_payment: PIPELINE_PHASE.PAYMENT,
  awaiting_approval: PIPELINE_PHASE.PAYMENT,
  payment_failed: PIPELINE_PHASE.PAYMENT,
  pending_confirmation: PIPELINE_PHASE.CONFIRMATION,
  confirmed: PIPELINE_PHASE.CONFIRMATION,
  scheduled: PIPELINE_PHASE.FULFILLMENT,
  shipped: PIPELINE_PHASE.FULFILLMENT,
  partially_delivered: PIPELINE_PHASE.FULFILLMENT,
  delivered: PIPELINE_PHASE.FULFILLMENT,
  completed: PIPELINE_PHASE.COMPLETED,
  cancelled: PIPELINE_PHASE.CANCELLED,
};

export const PIPELINE_PHASE_LABELS: Record<PipelinePhase, string> = {
  draft: "Draft",
  payment: "Payment",
  confirmation: "Confirmation",
  fulfillment: "Fulfillment",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const PIPELINE_STEPPER_STEPS = [
  {
    id: 0,
    label: "Payment",
    shortLabel: "Pay",
    statuses: [
      PIPELINE_STATUS.AWAITING_PAYMENT,
      PIPELINE_STATUS.AWAITING_APPROVAL,
      PIPELINE_STATUS.PAYMENT_FAILED,
    ],
  },
  {
    id: 1,
    label: "Confirmed",
    shortLabel: "Confirm",
    statuses: [
      PIPELINE_STATUS.PENDING_CONFIRMATION,
      PIPELINE_STATUS.CONFIRMED,
    ],
  },
  {
    id: 2,
    label: "Scheduled",
    shortLabel: "Sched",
    statuses: [PIPELINE_STATUS.SCHEDULED],
  },
  {
    id: 3,
    label: "Shipped",
    shortLabel: "Ship",
    statuses: [
      PIPELINE_STATUS.SHIPPED,
      PIPELINE_STATUS.PARTIALLY_DELIVERED,
    ],
  },
  {
    id: 4,
    label: "Delivered",
    shortLabel: "Deliv",
    statuses: [PIPELINE_STATUS.DELIVERED],
  },
  {
    id: 5,
    label: "Complete",
    shortLabel: "Done",
    statuses: [PIPELINE_STATUS.COMPLETED],
  },
] as const;

export type PipelineStepperStep = (typeof PIPELINE_STEPPER_STEPS)[number];

export function getPipelineStepIndex(status: PipelineStatus): number {
  if (status === PIPELINE_STATUS.CANCELLED) return -1;
  if (status === PIPELINE_STATUS.DRAFT) return -1;
  for (const step of PIPELINE_STEPPER_STEPS) {
    if (isOneOf(step.statuses, status)) return step.id;
  }
  return 0;
}

export interface OrderStatusAxes {
  stage: OrderStage;
  paymentStatus: OrderPaymentStatus;
  pricing?: { buyerGrandTotalCents: number };
}
